import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import SpecialInstruction from '../../../components/elements/SpecialInstruction'
import Loading from '../../../components/Loading'

export default function WithdrawLeadModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const dropdowns = useSelector(selectDropdowns);
  const dropdownOptions = useSelector(selectDropdownOptions);

  const leadWithdrawalCategory = dropdowns.data.filter((o) => o.code_key === 'lead_withdrawal_category')[0];
  const leadWithdrawalCategoryOptions = dropdownOptions.filter((o) => o.dropdown_id === leadWithdrawalCategory.id && o.is_active === 1).map((o) => ({ label: o.name, value: o.id, code_key: o.code_key }));
  const leadWithdrawalReason = dropdowns.data.filter((o) => o.code_key === 'lead_withdrawal_reason')[0];

  const { register, handleSubmit, control, watch, setValue, reset, formState: { errors }, } = useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const customerId = modal.options?.customerId
  const [codeKey, setCodeKey] = useState(false)
  const [leadOptions, setLeadOptions] = useState([])
  const inFlightCustomerId = useRef(null)

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAALetterText, setShowAALetterText] = useState(true);
  const [showReasonDropdown, setShowReasonDropdown] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const watchWithdrawalCategory = watch('lead_withdrawal_category_dropdown_option_id');
  const watchWithdrawalReason = watch('lead_withdrawal_reason_dropdown_option_id');

  // This modal stays mounted in the Footer between openings, so clear the previous customer's
  // leads and selections on every open — not only on Cancel — before loading this customer's leads.
  useEffect(() => {
    if (modal.is_active && modal.type === 'withdraw_lead_modal' && customerId) {
      resetForm()
      getWithdrawInfo(customerId)
    } else {
      inFlightCustomerId.current = null
    }
  }, [customerId, modal.is_active])

  useEffect(() => {
    if (selectedCategory) {
      const tempCodeKey = leadWithdrawalCategoryOptions.filter((o) => o.value === selectedCategory)[0].code_key;
      const tempOptions = dropdownOptions.filter((o) => o.dropdown_id === leadWithdrawalReason.id && o.optional_value_1 == tempCodeKey && o.is_active == 1 && o.optional_value_3 === 'TRUE').map((o) => ({ label: o.name + (o.optional_value_2 == 'TRUE' ? ' (send adverse action email)' : ''), value: o.id, optional_value_1: o.optional_value_1, code_key: o.code_key }));
      setValue('lead_withdrawal_reason_dropdown_option_id', null);
      setCodeKey(tempCodeKey)
      setShowReasonDropdown(tempOptions);
    } else {
      setShowReasonDropdown(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (watchWithdrawalCategory?.code_key == 'lead_withdrawal_category_customer' || watchWithdrawalReason?.code_key == 'lead_withdrawal_reason_error') {
      setShowAALetterText(false);
    } else {
      setShowAALetterText(true);
    }
    if (watchWithdrawalReason?.code_key === 'lead_withdrawal_reason_deceased') {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  }, [watchWithdrawalCategory, watchWithdrawalReason]);

  const getWithdrawInfo = async (forCustomerId) => {
    inFlightCustomerId.current = forCustomerId
    setLoading(true)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${forCustomerId}/withdraw_lead_info`)
    const response = await request.get()
    // Ignore a response that a close or a newer opening has superseded.
    if (inFlightCustomerId.current !== forCustomerId) return

    const leads = response?.data?.data?.leads ?? []
    setLeadOptions(leads.map((lead) => ({ label: `${lead.lead_guid} - ${lead.lead_status}`, value: lead.id })))
    setLoading(false)
  }

  const resetForm = () => {
    reset();
    setLeadOptions([]);
    setSelectedCategory(null);
    setCodeKey(false);
    setShowReasonDropdown(false);
    setShowAALetterText(true);
    setShowWarning(false);
    setSaving(false)
  };

  const handleCancel = () => {
    resetForm()
    dispatch(setModal({ is_active: false }));
  };

  const onSubmit = async (formData) => {
    setSaving(true)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/withdraw_lead`)
    try {
      formData.lead_withdrawal_category_dropdown_option_id = formData.lead_withdrawal_category_dropdown_option_id.value;
      formData.lead_withdrawal_reason_dropdown_option_id = formData.lead_withdrawal_reason_dropdown_option_id.value;
      formData.lead_id = formData.lead_id.value;
      const response = await request.modify(formData)
      if (response.status === 200 || response.status === 201) {
        modal.options.successCallback()
        handleCancel()
        dispatch(setNotification({ type: 'success', body: '' }))
      } else {
        modal.options.successCallback()
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      modal.options.successCallback()
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  };

  return modal.is_active && modal.type === 'withdraw_lead_modal' ? (
    <form onSubmit={handleSubmit(onSubmit)} id="withdraw_lead_modal_form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Withdraw Lead
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "110px", overflow: 'hidden' }}
          >
            {loading ? <Loading /> : (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <h3>Use this action when a customer no longer wants the loan they were seeking. If you do not see the
                    lead you want to withdraw that means the lead is not eligible to be withdrawn.</h3>
                </div>
                <div className="column">
                  <h3>Which lead do you want to withdraw? <span className="has-text-danger">*</span></h3>
                  <div className="mt-1">
                    <Controller
                      name="lead_id"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={leadOptions}
                          isSearchable={false}
                          classNames={reactSelectClassNames()}
                          onChange={(val) => {
                            field.onChange({ label: val?.label, value: val?.value })
                          }}
                          menuPosition="fixed"
                          styles={{
                            container: (base) => ({ ...base, width: '100%' }),
                            menu: ({ ...css }) => ({ ...css, width: '100%' })
                          }}
                          form="withdraw_lead_modal_form"
                        />
                      )}
                    />
                  </div>
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.lead_id &&
                      <span>{errors.lead_id.message ? errors.lead_id.message : 'Lead Selection Is Required'}</span>}
                  </p>

                  <h3 className="mt-3">Why is the lead being withdrawn? <span className="has-text-danger">*</span></h3>
                  <div className="mt-1">
                    <Controller
                      name="lead_withdrawal_category_dropdown_option_id"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={leadWithdrawalCategoryOptions}
                          isSearchable={false}
                          classNames={reactSelectClassNames()}
                          onChange={(val) => {
                            field.onChange({ label: val?.label, value: val?.value, code_key: val?.code_key, })
                            setSelectedCategory(val?.value)
                          }}
                          menuPosition="fixed"
                          styles={{
                            container: (base) => ({ ...base, width: '100%' }),
                            menu: ({ ...css }) => ({ ...css, width: '100%' })
                          }}
                          form="withdraw_lead_modal_form"
                        />
                      )}
                    />
                  </div>
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.lead_withdrawal_category_dropdown_option_id &&
                      <span>{errors.lead_withdrawal_category_dropdown_option_id.message ? errors.lead_withdrawal_category_dropdown_option_id.message : 'Lead withdrawal category is required'}</span>}
                  </p>
                  {codeKey && (
                    <>
                      <div className="mt-1">
                        <Controller
                          name="lead_withdrawal_reason_dropdown_option_id"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={showReasonDropdown}
                              isSearchable={false}
                              classNames={reactSelectClassNames()}
                              onChange={(val) => field.onChange({
                                label: val?.label,
                                value: val?.value,
                                code_key: val?.code_key,
                              })}
                              menuPosition="fixed"
                              styles={{
                                container: (base) => ({ ...base, width: '100%' }),
                                menu: ({ ...css }) => ({ ...css, width: '100%' })
                              }}
                              form="withdraw_lead_modal_form"
                            />
                          )}
                        />
                      </div>
                      <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                        {errors.lead_withdrawal_reason_dropdown_option_id &&
                          <span>{errors.lead_withdrawal_reason_dropdown_option_id.message ? errors.lead_withdrawal_reason_dropdown_option_id.message : 'Lead withdrawal reason is required'}</span>}
                      </p>
                    </>
                  )}
                </div>
                {showWarning && (
                  <div className="column">
                    <strong>Warning:</strong> By setting withdrawal reason to Deceased, the customer will be placed into
                    a non-fundable status with non-fundable deceased reason and their SSN will be added to the OEL.
                  </div>
                )}
                <SpecialInstruction
                  content={"<span>" +
                    "<span class='has-text-weight-bold'>This action will perform the following actions:</span><br>" +
                    "<span class='pl-2'>• Will set withdrawn_date</span> <br> " +
                    "<span class='pl-2'>• Will set lead_withdrawal_category</span> <br> " +
                    "<span class='pl-2'>• Will set lead_withdrawal_reason</span> <br> " +
                    "<span class='pl-2'>• Will set lead_status = withdrawn</span> <br> " +
                    (showAALetterText ? "<span class='pl-2'>• Will send an adverse action letter to the customer</span> <br>" : "") +
                    (showWarning ? "<span class='pl-2'>• Add customer SSN to OEL</span> <br>" : "") +
                    " </span>"}
                  type="is-warning"
                />
              </div>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button is-link is-small"
                disabled={saving && 'disabled'}
              >
                {saving ? 'Submitting' : 'Continue'}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
